import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * SmartCollectionTabContent
 *
 * Renders a transient assistant-query tab — the response that comes back
 * from POST /v1/assistant/query, opened as a new tab in the explorer.
 * Layout: a header with the assistant-chosen collection name and a
 * "Save collection" button, a short framing block, then the cards.
 *
 * Cards: we hydrate full BlockEnvelopes from the returned blockIds so the
 * existing `<Stream>` rendering applies (drag, selection, tools dock all
 * work the same). Compact `AssistantQueryBlock` summaries from the payload
 * are used as a fallback / first-paint signal but are not enough on their
 * own to render full block UI.
 *
 * The tab is transient until the user clicks Save: that path creates a real
 * Collection, copies the blocks into it, then swaps this tab's id to the
 * persisted collectionId so a hard reload preserves it.
 */
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { faSpinnerThird, faBookmark, faTrash, faChevronLeft, faCopy, faMessage, } from "@fortawesome/pro-solid-svg-icons";
import { FaIcon } from "@shared/client/components/fa-icon";
import { showToast } from "@shared/client/components/toast/show-toast";
import { Stream } from "@/components/stream/stream";
import { blockStore } from "@/data/block-store";
import { collectionStore } from "@/data/collection-store";
import { useAnnotationsForBlocks } from "@/data/use-annotations";
import { discardQuery, useAssistantQueryTab } from "@/data/assistant-query-store";
import { closeCollectionTab, replaceTabId } from "./app-tabs-store";
import { useGoBack } from "@/hooks/use-go-back";
import { ConfiguredLogger } from "@/lib/logger";
import { useUrlForCollectionId } from "@/lib/urls";
import { summarizeBlockForContext } from "@/lib/copy-context";
import { CollectionChatSidebar } from "@/components/collection-chat-sidebar/collection-chat-sidebar";
import "./smart-collection-tab-content.scss";
const logger = new ConfiguredLogger({ caller: "SmartCollectionTabContent" });
export const SmartCollectionTabContent = memo(({ transientId }) => {
    const navigate = useNavigate();
    const goBack = useGoBack();
    const buildUrlForCollectionId = useUrlForCollectionId();
    const tab = useAssistantQueryTab(transientId);
    const [isSaving, setIsSaving] = useState(false);
    // Gates the rerank-overflow reveal. Always starts collapsed so a tab
    // restored from sessionStorage opens with the primary stream first.
    const [isOverflowExpanded, setIsOverflowExpanded] = useState(false);
    // Gates the chat sidebar. Starts closed so the tab opens to the cards
    // first; "Ask about this collection" toggles it on. Now chats directly
    // against the transient block set via the `blockIds` variant of
    // `chatWithCollection` — no save-then-redirect required.
    const [isChatOpen, setIsChatOpen] = useState(false);
    const blockIds = tab?.payload?.blockIds ?? [];
    const overflowBlockIds = tab?.payload?.overflowBlockIds ?? [];
    // Sub-scope id for the rerank-dropped overflow blocks. Kept distinct
    // from the primary `transientId` scope so the two streams have
    // independent membership rows — important so the user can save just
    // the primary picks without inadvertently pulling overflow in too,
    // and so each Stream's surfaceKey stays unique (otherwise the dock /
    // selection state would collide).
    const overflowScopeId = useMemo(() => `${transientId}:overflow`, [transientId]);
    // Smart collections are scoped under the transient tab id, so the
    // assistant-returned id list participates in the same block-index
    // store as everything else. `loadBlocksByIds` upserts envelopes into
    // `blockStore` and writes membership rows preserving the assistant's
    // rank order; `useBlocks(transientId)` then drives the Stream below.
    const blocksList = collectionStore.useBlocks(transientId);
    const overflowBlocksList = collectionStore.useBlocks(overflowScopeId);
    useEffect(() => {
        if (blockIds.length === 0)
            return;
        void collectionStore.loadBlocksByIds(transientId, blockIds);
    }, [transientId, blockIds]);
    // Overflow only loads once the user expands it — most queries won't
    // need it, and lazily loading avoids hydrating up to ~18 extra blocks
    // for every smart-collection tab.
    useEffect(() => {
        if (overflowBlockIds.length === 0 || isOverflowExpanded === false)
            return;
        void collectionStore.loadBlocksByIds(overflowScopeId, overflowBlockIds);
    }, [overflowScopeId, overflowBlockIds, isOverflowExpanded]);
    const handleToggleOverflow = useCallback(() => {
        setIsOverflowExpanded((prev) => !prev);
    }, []);
    const handleClose = useCallback(() => {
        // Drop both scopes (primary + overflow) so a future tab with the
        // same transient id (extremely unlikely — they're cuids — but
        // defensible) re-fetches cleanly.
        collectionStore.removeAllBlocksFromScope(transientId);
        collectionStore.removeAllBlocksFromScope(overflowScopeId);
        closeCollectionTab(transientId);
        discardQuery(transientId);
        navigate("/");
    }, [transientId, overflowScopeId, navigate]);
    /**
     * Save → persist a real Collection from the transient payload, then
     * navigate to it. Replaces the transient tab id with the real one so
     * a hard reload preserves the destination.
     */
    const handleSave = useCallback(async () => {
        const payload = tab?.payload;
        if (!payload || isSaving)
            return;
        setIsSaving(true);
        try {
            const newCollectionId = await collectionStore.create({ title: payload.name });
            if (payload.blockIds.length > 0) {
                await collectionStore.copyBlocks(newCollectionId, payload.blockIds);
            }
            replaceTabId(transientId, newCollectionId, payload.name);
            discardQuery(transientId);
            showToast.success(`Saved “${payload.name}”`);
            navigate(buildUrlForCollectionId(newCollectionId));
        }
        catch (e) {
            const msg = e instanceof Error ? e.message : "Failed to save";
            logger.warn("Save collection failed", { error: msg });
            showToast.error(msg);
        }
        finally {
            setIsSaving(false);
        }
    }, [tab?.payload, isSaving, transientId, navigate, buildUrlForCollectionId]);
    // "Ask about this collection" — chats directly against the transient
    // block set via the new `blockIds` mode of `chatWithCollection`. No
    // save required; the sidebar persists its transcript per-block-set
    // (FNV hash of sorted ids) so reopening the same query reuses the
    // conversation.
    const handleAskAboutThis = useCallback(() => {
        setIsChatOpen((prev) => !prev);
    }, []);
    const handleCloseChat = useCallback(() => {
        setIsChatOpen(false);
    }, []);
    /**
     * Copy-to-clipboard: replicate the real-collection "Copy context as
     * JSON" payload for this transient set. The wrapper here is leaner
     * than a real collection's — no child collections, no slug / layer
     * / publishedAt — but the per-block summarization comes from the
     * same `summarizeBlockForContext` helper, so a downstream paste reads
     * the same way regardless of which surface produced it.
     *
     * Only includes blocks already hydrated in `blockStore`. A
     * smart-collection's blocks are loaded eagerly on mount, but if the
     * user mashes Copy on a tab that's still hydrating we skip the
     * unresolved rows (counted in the toast) rather than serialize empty
     * placeholders. No block cap — the assistant's reranked output is
     * naturally bounded (a few dozen rows at most) so there's nothing
     * to guard against.
     */
    const handleCopyContext = useCallback(() => {
        const payload = tab?.payload;
        if (!payload)
            return;
        const blockSummaries = [];
        let skippedUnresolved = 0;
        for (const row of blocksList.rows) {
            const envelope = blockStore.get(row.blockId);
            if (envelope === undefined) {
                skippedUnresolved += 1;
                continue;
            }
            blockSummaries.push(summarizeBlockForContext(envelope));
        }
        const json = JSON.stringify({
            // `type: "smart-collection"` distinguishes the paste shape from a
            // real-collection paste so downstream LLM prompts / tooling can
            // tell the two apart at a glance — same fields, different
            // provenance (an assistant-query result, not a user-curated set).
            type: "smart-collection",
            name: payload.name,
            framing: payload.framing,
            query: tab?.query ?? "",
            blocks: blockSummaries,
        }, null, 2);
        navigator.clipboard
            .writeText(json)
            .then(() => {
            const parts = [
                `${blockSummaries.length} block${blockSummaries.length === 1 ? "" : "s"}`,
            ];
            if (skippedUnresolved > 0) {
                parts.push(`${skippedUnresolved} still loading`);
            }
            showToast.success(`Copied (${parts.join(", ")})`);
        })
            .catch(() => showToast.error("Failed to copy context"));
    }, [tab, blocksList.rows]);
    // If the tab id isn't tracked anywhere (e.g. user landed on a stale
    // /collection/transient:... URL), bounce to the stream.
    useEffect(() => {
        if (!tab) {
            logger.info("Transient tab missing, redirecting", { transientId });
            navigate("/");
        }
    }, [tab, transientId, navigate]);
    // Helper: rows → envelopes. Stream wants `SimpleBlockEnvelope[]`
    // items; we synthesize lightweight placeholders for ids that haven't
    // hydrated yet (so cards always have *something* to render against
    // while `BlockStore` is fetching) and fall through to the cached
    // envelope when present. Shared between primary + overflow so any
    // future placeholder-shape changes only happen in one place.
    const buildEnvelopes = useCallback((rows) => rows.map((row) => {
        const cached = blockStore.get(row.blockId);
        if (cached)
            return cached;
        return {
            block: {
                id: row.blockId,
                type: "text",
                content: { text: "" },
                collectionId: "",
                createdBy: "",
                lineageId: "",
                title: "",
                summary: "",
                metadata: { channel: "viewer_client" },
                ingestorVersion: "0",
                processingStatus: "pending",
                capturedAt: row.sortKey,
                createdAt: row.sortKey,
                surfaceAt: row.sortKey,
            },
        };
    }), []);
    const envelopes = useMemo(() => buildEnvelopes(blocksList.rows), [buildEnvelopes, blocksList.rows]);
    const overflowEnvelopes = useMemo(() => buildEnvelopes(overflowBlocksList.rows), [buildEnvelopes, overflowBlocksList.rows]);
    // Single feed for the Stream — overflow tacks onto the end when the
    // user has expanded it, so the cards flow continuously through one
    // Stream instance instead of two stacked Streams separated by an
    // end-spacer. The toggle button (rendered via the Stream's `footer`
    // prop) stays at the very bottom of whichever set is showing.
    const streamItems = useMemo(() => (isOverflowExpanded ? [...envelopes, ...overflowEnvelopes] : envelopes), [envelopes, overflowEnvelopes, isOverflowExpanded]);
    // Warm the user-annotation collection for the hydrated envelopes so
    // each card's `BlockViewContainer` finds rows already in the cache.
    // Include overflow IDs only after expansion — most queries never
    // expand, and warming up to ~18 extra rows preemptively isn't free.
    const warmedBlockIds = useMemo(() => {
        if (isOverflowExpanded === false)
            return blockIds;
        return [...blockIds, ...overflowBlockIds];
    }, [blockIds, overflowBlockIds, isOverflowExpanded]);
    useAnnotationsForBlocks(warmedBlockIds);
    const hasOverflow = overflowBlockIds.length > 0;
    if (!tab)
        return null;
    // Header is identical in shape to a regular collection's header
    // (`.app-collection-header` etc., defined in app-screen.scss). The
    // difference is in the right-side actions: a real collection shows
    // Settings; a transient smart-collection shows Save + Close.
    const title = tab.status === "ready" && tab.payload ? tab.payload.name : truncate(tab.query, 60);
    const headerActions = (() => {
        if (tab.status === "loading")
            return null;
        if (tab.status === "error") {
            return (_jsxs("button", { type: "button", className: "app-collection-header__action", onClick: handleClose, children: [_jsx(FaIcon, { icon: faTrash }), _jsx("span", { children: "Close" })] }));
        }
        const payload = tab.payload;
        const hasBlocks = payload.blockIds.length > 0;
        // The icon-only buttons (Ask + Copy) mirror the real-collection
        // header's affordances. We disable them when the tab is empty or
        // still saving, and route Ask through the save-first flow so the
        // /assistant/collection-chat endpoint sees a persisted collection.
        return (_jsxs("div", { className: "smart-collection__actions", children: [_jsx("button", { type: "button", className: "app-collection-header__action app-collection-header__action--icon", onClick: handleAskAboutThis, disabled: !hasBlocks, "aria-label": isChatOpen ? "Close chat" : "Ask about this collection", "aria-pressed": isChatOpen, title: isChatOpen ? "Close chat" : "Ask about this collection", children: _jsx(FaIcon, { icon: faMessage }) }), _jsx("button", { type: "button", className: "app-collection-header__action app-collection-header__action--icon", onClick: handleCopyContext, disabled: !hasBlocks, "aria-label": "Copy collection context as JSON", title: "Copy collection context as JSON", children: _jsx(FaIcon, { icon: faCopy }) }), _jsxs("button", { type: "button", className: "app-collection-header__action", onClick: () => void handleSave(), disabled: isSaving || !hasBlocks, children: [_jsx(FaIcon, { icon: isSaving ? faSpinnerThird : faBookmark }), _jsx("span", { children: isSaving ? "Saving…" : "Save collection" })] }), _jsxs("button", { type: "button", className: "app-collection-header__action", onClick: handleClose, children: [_jsx(FaIcon, { icon: faTrash }), _jsx("span", { children: "Close" })] })] }));
    })();
    const body = (() => {
        if (tab.status === "loading") {
            return (_jsxs("div", { className: "smart-collection__skeleton", children: [_jsx(FaIcon, { icon: faSpinnerThird, className: "loading-spinner" }), _jsx("p", { children: "Putty is rummaging\u2026" })] }));
        }
        if (tab.status === "error") {
            return (_jsx("p", { className: "smart-collection__error", children: tab.errorMessage ?? "Something went wrong." }));
        }
        const payload = tab.payload;
        // `.smart-collection__body` is a single page-scrollable container
        // that holds the framing + Stream. Inside it we override
        // `.stream`'s default `flex: 1 1 0` (Stream's normal
        // claim-all-flex-space behavior) so the Stream sizes to its
        // natural content height — the body's own `overflow-y: auto`
        // handles scroll for the whole flow.
        //
        // Show-more behavior: overflow envelopes append onto the Stream's
        // items array (`streamItems`) when expanded, so the cards extend
        // the existing feed instead of mounting a second Stream beneath
        // it. The toggle button is the Stream's `footer`, so it lives
        // inside the Stream's own scroll context right after the last
        // card and just above the standard 140px end-spacer; its label
        // flips between "Show N more" and "Show fewer" based on
        // `isOverflowExpanded`.
        const overflowToggle = hasOverflow ? (_jsx("button", { type: "button", className: "smart-collection__overflow-toggle", onClick: handleToggleOverflow, "aria-expanded": isOverflowExpanded, children: isOverflowExpanded
                ? "Show fewer results"
                : `Show ${overflowBlockIds.length} more result${overflowBlockIds.length === 1 ? "" : "s"}` })) : undefined;
        return (_jsxs("div", { className: "smart-collection__body", children: [payload.framing ? _jsx("p", { className: "smart-collection__framing", children: payload.framing }) : null, payload.blockIds.length === 0 ? (_jsx("p", { className: "app-screen__status", children: "Nothing in this one." })) : blocksList.isLoading && blocksList.rows.length === 0 ? (_jsx("div", { className: "loading-spinner-container", children: _jsx(FaIcon, { icon: faSpinnerThird, className: "loading-spinner" }) })) : (_jsx(Stream, { data: {
                        items: streamItems,
                        isLoading: false,
                        hasMore: false,
                        isFetchingNextPage: false,
                        fetchMore: () => { },
                    }, columns: 2, groupBy: "none", surfaceKey: `smart:${transientId}`, footer: overflowToggle }))] }));
    })();
    // Sidebar mounts only when chat is open AND the payload is ready —
    // before then there's no block set to anchor the conversation to.
    // We pass the assistant-query's `name` (used as the tab title) as the
    // sidebar's `collectionTitle`, which the server feeds into the
    // prompt's `<collection_title>` tag — same shape it sees for a real
    // Collection chat, just with a transient title.
    const chatPayload = tab.status === "ready" ? tab.payload : undefined;
    return (_jsxs("div", { className: "app-collection", children: [_jsxs("div", { className: "app-collection-header", children: [_jsxs("div", { className: "app-collection-header__title-group", children: [_jsx("button", { type: "button", className: "app-collection-header__back", onClick: goBack, "aria-label": "Back", children: _jsx(FaIcon, { icon: faChevronLeft }) }), _jsx("h1", { className: "app-collection-header__title", children: title })] }), headerActions] }), body, isChatOpen && chatPayload && chatPayload.blockIds.length > 0 && (_jsx(CollectionChatSidebar, { blockIds: chatPayload.blockIds, collectionTitle: chatPayload.name, onClose: handleCloseChat }))] }));
});
function truncate(s, max) {
    return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}
